import { ShieldCheck, MessageSquare, Star } from "lucide-react";
import Seo, { breadcrumbSchema } from "../components/Seo";
import { Reveal, Overline } from "../components/Reveal";
import ConsultationForm from "../components/ConsultationForm";
import { useTestimonials } from "../lib/hooks";

export default function Consultation() {
  const { data: testimonials } = useTestimonials();
  const review = (testimonials || [])[0];

  return (
    <div data-testid="consultation-page">
      <Seo
        title="Request a Consultation | NOOR Design & Build"
        description="Request a free initial consultation for your extension, renovation, conversion or new build — we reply within one working day."
        path="/request-a-consultation"
        schema={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Request a Consultation", path: "/request-a-consultation" }])}
      />
      <section className="bg-graphite text-white blueprint-grid">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 pt-32 md:pt-44 pb-16 md:pb-24">
          <Overline light>[ Request a Consultation ]</Overline>
          <h1 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 max-w-3xl leading-[1.05]">
            Planning a Serious Home Transformation?
          </h1>
          <p className="text-white/60 text-base md:text-lg leading-relaxed mt-8 max-w-2xl">
            Tell us about your project and we'll get back to you within one working day.
          </p>
        </div>
      </section>
      <section className="bg-cream blueprint-grid-light">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 py-16 md:py-24 grid lg:grid-cols-12 gap-10">
          <Reveal className="lg:col-span-8">
            <Overline>[ The Form ]</Overline>
            <h2 className="font-display font-light text-3xl tracking-tight mt-3 mb-8">Tell us about your project.</h2>
            <ConsultationForm />
          </Reveal>
          <Reveal delay={0.1} className="lg:col-span-4">
            <aside className="space-y-px border border-black/10 bg-black/10" data-testid="consultation-trust-panel">
              <div className="bg-cream p-7 flex items-start gap-4">
                <ShieldCheck size={20} strokeWidth={1.25} className="text-ember shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-medium">10-Year Workmanship Guarantee</p>
                  <p className="text-sm text-graphite/55 leading-relaxed mt-1.5">Workmanship we are prepared to guarantee for ten years — because we do not expect to be called back.</p>
                </div>
              </div>
              <div className="bg-cream p-7 flex items-start gap-4">
                <MessageSquare size={20} strokeWidth={1.25} className="text-ember shrink-0 mt-0.5" />
                <div>
                  <p className="font-display font-medium">Free Consultation</p>
                  <p className="text-sm text-graphite/55 leading-relaxed mt-1.5">An honest first conversation about your project — no charge, no obligation.</p>
                </div>
              </div>
              {review && (
                <figure className="bg-cream p-7" data-testid="consultation-review">
                  <div className="flex items-center gap-1" aria-label="Five star Google review">
                    {[0, 1, 2, 3, 4].map((s) => (
                      <Star key={s} size={13} className="text-[#D4A017]" fill="#D4A017" strokeWidth={0} />
                    ))}
                  </div>
                  <blockquote className="serif-accent text-graphite/80 leading-relaxed mt-4">“{review.text}”</blockquote>
                  <figcaption className="font-mono text-[10px] tracking-[0.25em] uppercase text-graphite/40 mt-4">
                    {review.name}{review.location ? ` — ${review.location}` : ""} · Google review
                  </figcaption>
                </figure>
              )}
            </aside>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
